import { AxiosResponse } from "axios";
import { api, CustomAxiosRequestConfig, ENDPOINTS } from "@app/api";
import {
  MachinesRequestDef,
  Machine,
  machineSummaryRequestDef,
} from "@app/types";

export const apiMachineInfo = (
  groupId: string,
  machineId: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.machineInfo(groupId, machineId), {
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const apiMachineReports = (
  groupId: string,
  machineId: string,
  params?: MachinesRequestDef,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.machineReport(groupId, machineId), {
    params,
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const apiPostMachineReport = (
  groupId: string,
  machineId: string,
  payload: Record<string, unknown>,
): Promise<AxiosResponse> => {
  return api.post(ENDPOINTS.machineReport(groupId, machineId), payload);
};

export const apiPostFuelMaintenanceReport = (
  groupId: string,
  machineId: string,
  payload: Record<string, unknown>,
): Promise<AxiosResponse> => {
  return api.post(
    ENDPOINTS.fuelMaintenanceReportNew(groupId, machineId),
    payload,
  );
};

export const apiPutFuelMaintenanceReport = (
  groupId: string,
  machineId: string,
  machineReportId: string,
  payload: Record<string, unknown>,
): Promise<AxiosResponse> => {
  return api.put(
    ENDPOINTS.machineFuelMaintenanceReport(groupId, machineId, machineReportId),
    payload,
  );
};

export const apiPostMaintenanceReport = (
  groupId: string,
  machineId: string,
  payload: Record<string, unknown>,
): Promise<AxiosResponse> => {
  return api.post(ENDPOINTS.maintenanceReportNew(groupId, machineId), payload);
};

export const apiPutMaintenanceReport = (
  groupId: string,
  machineId: string,
  machineReportId: string,
  payload: Record<string, unknown>,
): Promise<AxiosResponse> => {
  return api.put(
    ENDPOINTS.maintenanceReport(groupId, machineId, machineReportId),
    payload,
  );
};

export const apiPostMaintenanceReportDraft = (
  groupId: string,
  machineId: string,
  payload: Record<string, unknown>,
): Promise<AxiosResponse> => {
  return api.post(
    ENDPOINTS.maintenanceReportDraft(groupId, machineId),
    payload,
  );
};

export const apiGetMaintenanceReportDraft = (
  groupId: string,
  machineId: string,
  machineReportId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.maintenanceReportDraftDetail(groupId, machineId, machineReportId),
    { skipGlobalLoader: true } as CustomAxiosRequestConfig,
  );
};

export const apiGetMaintenanceReportDraftList = (
  groupId: string,
  machineId: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.maintenanceReportDraftList(groupId, machineId), {
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const apiDeleteMaintenanceReportDraft = (
  groupId: string,
  machineId: string,
  machineReportId: string,
): Promise<AxiosResponse> => {
  return api.delete(
    ENDPOINTS.maintenanceReportDraftDetail(groupId, machineId, machineReportId),
  );
};

export const apiGetMachineReportDetail = (
  groupId: string,
  machineId: string,
  machineReportId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.machineReportDetail(groupId, machineId, machineReportId),
  );
};

export const apiDeleteMachineReport = (
  groupId: string,
  machineId: string,
  machineReportId: string,
): Promise<AxiosResponse> => {
  return api.delete(
    ENDPOINTS.machineReportDetail(groupId, machineId, machineReportId),
  );
};

export const apiPutMachineReport = (
  groupId: string,
  machineId: string,
  machineReportId: string,
  payload: Record<string, unknown>,
): Promise<AxiosResponse> => {
  return api.put(
    ENDPOINTS.machineReportDetail(groupId, machineId, machineReportId),
    payload,
  );
};

export const apiGetMachineReportOriginal = (
  groupId: string,
  machineId: string,
  machineReportId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.machineReportOriginal(groupId, machineId, machineReportId),
    { skipGlobalLoader: true } as CustomAxiosRequestConfig,
  );
};

export const apiGetMachinesDetails = (
  groupId: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.machineDetails(groupId));
};

export const apiGetGroupMachinesList = (
  groupId: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.groupMachinesDetailsList(groupId), {
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const apiGetMachinesTemplatePathUrl = (): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.machineTemplatePathUrl);
};

export const apiMachineReportInfo = (
  groupId: string,
  machineId: string,
  startDate: string,
  endDate: string,
  utc?: string,
  search?: string,
  timezoneOffset?: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.machineReportInfo(groupId, machineId), {
    params: {
      startDate,
      endDate,
      utc,
      ...(search ? { search } : {}),
      ...(timezoneOffset ? { timezoneOffset } : {}),
    },
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const apiMachineSummary = (
  groupId: string,
  machineId: string,
  params: machineSummaryRequestDef,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.machineSummary(groupId, machineId), {
    params,
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const getMachineSummary = (
  groupId: string,
  machineId: string,
  params: machineSummaryRequestDef,
) => {
  return apiMachineSummary(groupId, machineId, params).then((db) => {
    const { data, meta }: { data: any; meta: any } = db.data;

    return { data, meta };
  });
};

export const getMachineReportInfo = (
  groupId: string,
  machineId: string,
  startDate: string,
  endDate: string,
  utc?: string,
  search?: string,
  timezoneOffset?: string,
) => {
  return apiMachineReportInfo(
    groupId,
    machineId,
    startDate,
    endDate,
    utc,
    search,
    timezoneOffset,
  ).then((db) => {
    const { data } = db.data;
    return data;
  });
};

export const getMachineReportDetail = (
  groupId: string,
  machineId: string,
  machineReportId: string,
) => {
  return apiGetMachineReportDetail(groupId, machineId, machineReportId).then(
    (db) => {
      const { data } = db.data;
      return data;
    },
  );
};

export const getMachineInfo = (groupId: string, machineId: string) => {
  return apiMachineInfo(groupId, machineId).then((db) => {
    const { data } = db.data;
    return data;
  });
};

export const getMachineReports = (
  groupId: string,
  machineId: string,
  params?: MachinesRequestDef,
) => {
  return apiMachineReports(groupId, machineId, params).then((db) => {
    const { data, meta }: { data: Machine[]; meta: any } = db.data;
    return { data, meta };
  });
};

export const apiGetMachineReportResponses = (
  groupId: string,
  machineId: string,
  machineReportId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.machineReportResponses(groupId, machineId, machineReportId),
    { skipGlobalLoader: true } as CustomAxiosRequestConfig,
  );
};

export const apiPostMachineReportResponses = (
  groupId: string,
  machineId: string,
  machineReportId: string,
  param: any,
): Promise<AxiosResponse> => {
  return api.post(
    ENDPOINTS.machineReportResponses(groupId, machineId, machineReportId),
    param,
  );
};

export const apiMachineReportStatus = (
  groupId: string,
  machineId: string,
  machineReportId: string,
  param: any,
): Promise<AxiosResponse> => {
  return api.put(
    ENDPOINTS.machineReportStatus(groupId, machineId, machineReportId),
    param,
  );
};

export const apiMachineReportActionChoices = (): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.machineReportActionChoices(), {
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const updateMachineReportStatus = (
  groupId: string,
  machineId: string,
  machineReportId: string,
  isRead: boolean,
) => {
  return apiMachineReportStatus(groupId, machineId, machineReportId, {
    isRead,
  });
};

export const apiGetMachineOperationReport = (
  groupId: string,
  machineId: string,
  machineReportId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.machineOperationReport(groupId, machineId, machineReportId),
  );
};

export const apiGetFuelMaintenanceReport = (
  groupId: string,
  machineId: string,
  machineReportId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.machineFuelMaintenanceReport(groupId, machineId, machineReportId),
    { skipGlobalLoader: true } as CustomAxiosRequestConfig,
  );
};

export const apiGetMaintenanceReport = (
  groupId: string,
  machineId: string,
  machineReportId: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.maintenanceReport(groupId, machineId, machineReportId),
    {
      skipGlobalLoader: true,
    } as CustomAxiosRequestConfig,
  );
};

export const apiGetInspectionItemRecords = (
  groupId: string,
  machineId: string,
  inspectionId: string,
  timezone: string,
): Promise<AxiosResponse> => {
  return api.get(
    ENDPOINTS.inspectionItemRecords(groupId, machineId, inspectionId, timezone),
    {
      skipGlobalLoader: true,
    } as CustomAxiosRequestConfig,
  );
};
